import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Mail,
  Phone,
  BadgeCheck,
  UserCircle2,
  CalendarDays,
  IdCard,
  Clock,
  Copy,
  ExternalLink,
  Power,
  ChevronRight,
  Briefcase,
  Monitor,
  Search,
  FileText,
  User,
  MailCheck,
  PhoneCall,
  Play,
  Pause,
  MessageSquare,
  Flag,
  ChevronDownIcon,
  Paperclip,
  CircleDotDashed,
  CalendarCheck,
} from "lucide-react";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import { Alert, Skeleton, Snackbar } from "@mui/material";
import { GlobalContext } from "../../../../../context/Context";
import CommentsSection from "../../../../../component/comment";
import Tooltip from "../../../../../component/ui/tooltip";
import StatusChip from "../../../../../component/ui/chip/status";
import FilesTab from "../../../../../component/files";
import Avatar from "../../../../../component/ui/avatar";

const tabs = [
  { key: "summary", label: "Summary" },
  { key: "comments", label: "Comments" },
  { key: "call", label: "Calls" },
  { key: "emails", label: "Emails" },
  { key: "sms", label: "Sms" },
  { key: "files", label: "Files" },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const formatStatus = (status) => {
  if (!status) return "";

  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone],
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">
            {title.includes("_")
              ? title.split("_").map((word) => ` ${word}`)
              : title}
          </div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
      {/* {tone !== "default" && (
        <div className="absolute right-3 top-3">
          <Badge tone={tone}>{tone === "green" ? "Good" : tone === "amber" ? "Watch" : "Check"}</Badge>
        </div>
      )} */}
    </div>
  );
}

function Field({ label, value, icon: Icon, copyable, link }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className="truncate">{value}</span>
        )}
        {copyable && (
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(String(value ?? ""));
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch {}
            }}
            className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
          >
            <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SubTaskQuickView({
  open = true,
  onClose = () => {},
  data,
}) {
  // console.log("Data", data)
  const [lead, setLead] = useState({});
  const [activeTab, setActiveTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(lead?.task_status);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);
  const [alertData, setAlertData] = useState({ isShow: false });

  const GetData = async (id) => {
    try {
      let call = await apiCall({
        method: "get",
        url: `subtasks/get-subtask/${id}`,
      });
      let taskData = call?.data;

      setIsLoading(true);

      const sdata = {
        id: taskData?.sub_task_id,
        name: taskData?.sub_task_name,
        description: taskData?.sub_task_description,
        task_status: taskData?.sub_task_status,
        is_qa: taskData?.is_qa,
        deadline: taskData?.deadline,
        priority: taskData?.priority,
        source: taskData?.source,
        is_active: taskData?.is_active,
        created_at: taskData?.created_at,
        project_key: taskData?.project_key,
        task_key: taskData?.task_key,
        sub_task_key: taskData?.sub_task_key,
        assigned_users: taskData?.assigned_users,
        attachments: taskData?.attachments,
        attachments: taskData?.attachments,
      };

      setStatus(taskData?.sub_task_status);
      setLead(sdata);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // console.log("lead in fields", lead);

  const fields = useMemo(
    () => [
      { tab: "summary", label: "ID", value: lead?.id ?? "-", icon: IdCard },
      { tab: "summary", label: "Name", value: lead?.name ?? "-", icon: User },
      {
        tab: "summary",
        label: "Description",
        value: lead?.description ?? "-",
        icon: FileText,
      },
      {
        tab: "summary",
        label: "Priority",
        value: lead?.priority ?? "-",
        icon: Flag,
      },
      {
        tab: "summary",
        label: "Source",
        value: lead?.source ?? "-",
        icon: CircleDotDashed,
      },
      {
        tab: "summary",
        label: "Project Key",
        value: lead?.project_key ?? "-",
        icon: IdCard,
      },
      {
        tab: "summary",
        label: "Task Key",
        value: lead?.task_key ?? "-",
        icon: IdCard,
      },
      {
        tab: "summary",
        label: "Deadline",
        value: lead?.deadline ?? "-",
        icon: CalendarCheck,
      },

      {
        tab: "summary",
        label: "Assigned",
        value: (
          <div className="flex -space-x-2">
            {Array.isArray(lead?.assigned_users) &&
            lead.assigned_users.length > 0 ? (
              lead.assigned_users.map((each, idx) => (
                <Tooltip
                  key={idx}
                  content={each?.user?.name}
                  position="top center"
                >
                  <div className={`relative ${idx !== 0 ? "-ml-0" : ""}`}>
                    {/* {each?.user?.userimg ? (
                      <img
                        src={each?.user?.userimg}
                        alt={each?.user?.name}
                        className="h-8 w-8 rounded-full border-2 border-white object-cover object-center shadow-sm bg-white"
                        onError={(e) => {
                          e.target.onerror = null; // prevent infinite loop
                          e.target.style.display = "none";
                          const fallback = document.createElement("div");
                          fallback.className =
                            "h-8 w-8 flex items-center justify-center rounded-full bg-gray-300 text-black text-xs font-medium border-2 border-white shadow-sm";
                          fallback.innerText = (
                            (each?.user?.name?.split(" ")[0]?.[0] || "") +
                            (each?.user?.name?.split(" ")[1]?.[0] || "")
                          ).toUpperCase();
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    ) : (
                      <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-300 text-black text-xs font-medium border-2 border-white shadow-sm">
                        {(each?.user?.name?.split(" ")[0]?.[0] || "") +
                          (each?.user?.name?.split(" ")[1]?.[0] || "")}
                      </div>
                    )} */}
                    <Avatar
                      src={each?.user?.userimg}
                      title={each?.user?.name}
                      className="border-2 border-gray-200 shadow-sm "
                    />
                  </div>
                </Tooltip>
              ))
            ) : (
              <span className="text-gray-400 text-sm">-</span>
            )}
          </div>
        ),
        icon: UserCircle2,
      },
    ],
    [lead, status],
  );

  const filteredFields = fields.filter(
    (f) =>
      (!query ||
        `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
      (activeTab === "summary"
        ? ["summary", "work", "personal"].includes(f.tab)
        : f.tab === activeTab),
  );

  useEffect(() => {
    GetData(data?.sub_task_id);
  }, [data]);

  const [openAccountIndex, setOpenAccountIndex] = useState(0);

  const [showAll, setShowAll] = useState(false);
  const visibleUsers = showAll
    ? lead?.assigned_users
    : lead?.assigned_users?.slice(0, 2);
  const hiddenCount = lead?.assigned_users?.length - 3;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[800px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
              aria-modal
              role="dialog"
            >
              {isLoading ? (
                <>
                  {/* Header */}
                  <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex items-center justify-center size-12 bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 rounded-full overflow-hidden">
                          <p className="capitalize font-semi-bold text-base text-black">
                            {lead?.name?.split(" ")?.map((n, i) => n.charAt(0))}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                              {lead?.name}
                            </h2>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                            <Badge
                              tone={
                                status == "pending" || status == "in_process"
                                  ? "blue"
                                  : status == "completed"
                                    ? "green"
                                    : "red"
                              }
                              icon={BadgeCheck}
                            >
                              {status}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                          onClick={onClose}
                          aria-label="Close"
                        >
                          <X className="size-4" /> Exit
                        </button>
                      </div>
                    </div>

                    {/* Quick actions */}
                    {/* <div className="mt-4 flex flex-wrap items-center gap-2">
                      <QuickAction
                        icon={Mail}
                        label="Email"
                        href={`mailto:${lead?.email}`}
                      />
                      <QuickAction
                        icon={Phone}
                        label="Call"
                        href={`tel:${lead?.phone}`}
                      />

                      <div className="ml-auto flex items-center gap-2">
                        <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">
                          ⌘/Ctrl
                        </kbd>
                        <span className="text-[10px] text-slate-500">
                          + F to search
                        </span>
                      </div>
                    </div> */}
                  </div>

                  {/* Body */}
                  <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                    {/* Left: main content */}
                    <div className="col-span-12 flex min-h-0 flex-col gap-4">
                      {/* Top stats */}
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {lead?.stats?.map((ele, idx) => {
                          return (
                            <Stat
                              title={ele?.key}
                              value={ele?.value}
                              icon={CalendarDays}
                            />
                          );
                        })}
                      </div>

                      {/* Tabs */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {tabs?.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => setActiveTab(t.key)}
                              className={classNames(
                                "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                                activeTab === t.key
                                  ? "bg-[#F5F5F5] text-[#0E121B]"
                                  : "text-slate-600 hover:bg-slate-100",
                              )}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                        {/* <div className="relative">
                          <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                          <input
                            id="quickview-search"
                            placeholder="Search fields…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                          />
                        </div> */}
                      </div>

                      <div className="h-full overflow-auto rounded-2xl py-2">
                        {/* Render fields for current tab (or summary) */}
                        <div className="grid grid-cols-1 gap-3">
                          {filteredFields?.map((f, idx) => (
                            <Field key={idx} {...f} />
                          ))}
                        </div>

                        {activeTab === "comments" && (
                          <CommentsSection
                            type="subTask"
                            id={lead?.id}
                            apiBaseUrl="subTask"
                            title="true"
                            setAlertData={setAlertData}
                          />
                        )}

                        {activeTab === "call" && (
                          <TabPlaceholder
                            icon={PhoneCall}
                            title="Calls"
                            message="We are working on the call logs and recordings section."
                          />
                        )}

                        {activeTab === "emails" && (
                          <TabPlaceholder
                            icon={Mail}
                            title="Emails"
                            message="Email activity and history will be available soon."
                          />
                        )}

                        {activeTab === "sms" && (
                          <TabPlaceholder
                            icon={MessageSquare}
                            title="SMS"
                            message="SMS communication history is under development."
                          />
                        )}

                        {activeTab === "files" && (
                          <FilesTab
                            leadId={lead?.id}
                            attachments={lead?.attachments}
                            attachmenttype="subTask"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <IdCard className="size-4" /> ID {data?.sub_task_id}
                      {/* <span>-</span>
                      <CalendarDays className="size-4" />  {lead?.updated_at} */}
                    </div>
                  </div>
                </>
              ) : (
                <InnerLoader wrapperClass={"w-full"} />
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-2xl bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}
